/**
 * Export vers Excel — structure groupée par mois > référence > lignes
 */
export function exportToExcelSimple(filteredSalesData: any[], customer: string): void {
  const htmlTable   = generateFormattedTableHTML(filteredSalesData);
  const htmlContent = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office"
          xmlns:x="urn:schemas-microsoft-com:office:excel"
          xmlns="http://www.w3.org/TR/REC-html40">
    <head>
      <meta charset="UTF-8">
      <style>
        /* ── Base uniforme ── */
        body, table, td, th {
          font-family: Arial, sans-serif;
          font-size: 11px;        /* taille unique pour tout le document */
        }

        table { border-collapse: collapse; width: 100%; }

        /* ── En-têtes colonnes ── */
        th {
          background-color: #4F81BD;
          color: white;
          font-weight: bold;
          font-size: 11px;        /* même taille, pas plus grand */
          text-align: center;
          padding: 6px 8px;
          border: 1px solid #ccc;
        }

        td { padding: 5px 8px; border: 1px solid #ddd; }

        /* ── En-tête de mois ── */
        .month-header td {
          background-color: #1a7fa0;
          color: white;
          font-weight: bold;
          font-size: 11px;        /* ← était 13px, ramené à 11px */
          border-top: 2px solid #0e5f7a;
        }

        /* ── En-tête de référence ── */
        .ref-header td {
          background-color: #dbeafe;
          color: #1d4ed8;
          font-weight: bold;
          font-size: 11px;        /* uniforme */
          border-top: 1px solid #93c5fd;
        }
        .ref-header .ref-badge {
          font-weight: 800;
          font-size: 11px;        /* ← plus de taille spéciale */
        }
        .text-muted {
          color: #6b7280;
          font-size: 11px;        /* ← était 11px mais explicite */
        }

        /* ── Lignes détail ── */
        .sale-item td {
          background-color: #fafcff;
          font-size: 11px;
          padding-left: 16px;
        }
        .sale-item:nth-child(even) td { background-color: #f0f7ff; }

        /* ── Total global ── */
        .grand-total td {
          background-color: #1d4ed8;
          color: white;
          font-weight: bold;
          font-size: 11px;        /* ← était 13px */
          text-align: right;
          border-top: 2px solid #1e3a8a;
        }

        /* ── Titre du rapport ── */
        .report-title {
          font-size: 13px;        /* seul élément légèrement plus grand */
          font-weight: bold;
          color: #1F497D;
          margin-bottom: 12px;
        }

        .text-right  { text-align: right;  }
        .text-center { text-align: center; }
      </style>
    </head>
    <body>
      <div class="report-title">
        ${customer} — Rapport des Ventes &nbsp;|&nbsp;
        ${new Date().toLocaleDateString('fr-FR', { day:'2-digit', month:'long', year:'numeric' })}
      </div>
      ${htmlTable}
    </body>
    </html>
  `;

  downloadExcelHTML(
    htmlContent,
    `Export_Ventes_${customer}_${new Date().toISOString().split('T')[0]}.xls`
  );
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Génère le tableau HTML à partir de la structure groupée :
 *   month-header → sale-ref-header (+ lines[]) → sale-item
 */
function generateFormattedTableHTML(data: any[]): string {

  // Accumulateurs globaux
  let grandTotalHt  = 0;
  let grandTotalTva = 0;
  let grandTotalTtc = 0;

  let html = `
    <table>
      <thead>
        <tr>
          <th style="min-width:90px;">Date</th>
          <th style="min-width:110px;">Réf. Vente</th>
          <th style="min-width:160px;">Désignation</th>
          <th style="min-width:120px;">Famille</th>
          <th class="text-right" style="min-width:100px;">Prix U. (MAD)</th>
          <th class="text-center" style="min-width:70px;">Qté</th>
          <th class="text-right" style="min-width:100px;">Total HT (MAD)</th>
          <th class="text-right" style="min-width:90px;">TVA (MAD)</th>
          <th class="text-right" style="min-width:110px;">Total TTC (MAD)</th>
          <th style="min-width:120px;">Faite par</th>
          <th style="min-width:90px;">Origine</th>
        </tr>
      </thead>
      <tbody>
  `;

  for (const row of data) {

    // ── En-tête de mois ─────────────────────────────────────────
    if (row.rowType === 'month-header') {
      html += `
        <tr class="month-header">
          <td colspan="8"><strong>📅 ${row.designation}</strong></td>
          <td class="text-right" colspan="3">
            Total TTC du mois :&nbsp;<strong>${formatCurrency(row.monthTotal)}</strong>
          </td>
        </tr>
      `;
      continue;
    }

    // ── En-tête de référence ─────────────────────────────────────
    if (row.rowType === 'sale-ref-header') {
      const nbLines = row.lines?.length ?? 0;
      const date    = row.saleDate
        ? new Date(row.saleDate).toLocaleDateString('fr-FR')
        : '';

      html += `
        <tr class="ref-header">
          <td style="text-align:start !important;float:left;">${date}</td>
          <td colspan="3">
            <span class="ref-badge">🔖 ${row.saleRef || '---'}</span>
            &nbsp;
            <span class="text-muted">${row.faitePar || ''} · ${nbLines} ligne(s)</span>
          </td>
          <td colspan="3" class="text-right">
            <span class="text-muted">Total HT :</span>&nbsp;
            <strong>${formatCurrency(row.totalHt)}</strong>
          </td>
          <td colspan="1" class="text-right">
            —
          </td>
          <td colspan="2" class="text-right">
            <span class="text-muted">Total TTC :</span>&nbsp;
            <strong>${formatCurrency(row.totalTtc)}</strong>
          </td>
        </tr>
      `;

      // ── Lignes détail ──────────────────────────────────────────
      if (row.lines && row.lines.length > 0) {
        row.lines.forEach((line: any) => {
          const lineDate = line.saleDate
            ? new Date(line.saleDate).toLocaleString('fr-FR', {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit'
              })
            : '';

          const ht  = line.totalHt  || 0;
          const tva = line.tva      || 0;
          const ttc = line.totalTtc || 0;

          grandTotalHt  += ht;
          grandTotalTva += tva;
          grandTotalTtc += ttc;

          html += `
            <tr class="sale-item">
              <td class="text-muted">${lineDate}</td>
              <td class="text-muted">${line.saleRef || '---'}</td>
              <td>${line.designation || '---'}</td>
              <td>${line.famille     || '---'}</td>
              <td class="text-right">${formatCurrency(line.prixUnitaire)}</td>
              <td class="text-center">${line.quantite ?? '---'}</td>
              <td class="text-right">${formatCurrency(ht)}</td>
              <td class="text-right">${formatCurrency(tva)}</td>
              <td class="text-right"><strong>${formatCurrency(ttc)}</strong></td>
              <td>${line.faitePar || '---'}</td>
              <td>${line.origine  || '---'}</td>
            </tr>
          `;
        });
      }

      continue;
    }
  }

  // ── Total global ───────────────────────────────────────────────
  html += `
    <tr class="grand-total">
      <td colspan="6">TOTAL GLOBAL</td>
      <td>${formatCurrency(grandTotalHt)}</td>
      <td>${formatCurrency(grandTotalTva)}</td>
      <td>${formatCurrency(grandTotalTtc)}</td>
      <td colspan="2"></td>
    </tr>
  `;

  html += '</tbody></table>';
  return html;
}

function formatCurrency(value: number | null | undefined): string {
  if (!value && value !== 0) return '0,00 MAD';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'MAD',
    minimumFractionDigits: 2
  }).format(value);
}

function downloadExcelHTML(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'application/vnd.ms-excel' });
  const url  = window.URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}